import axios from 'axios';

import { kitRenderCostFor, type CreditPricing } from '@/store/slices/aiCredits';
import type { AdGenerateRequest } from '@/store/slices/creativeJobs';

import { normaliseLocalizedCopy, type AdLanguageId, type LocalizedCopy } from './ad-language';
import { creativesRequestConfig, creativesServiceBase, type CreativeVersion } from './creative-api';
import { isNonJsonBody } from './video-library-api';
import type { VideoSubjectType } from './video-subjects';

/**
 * Banner Kits: one AI-rendered ad across every social and ad-network size. A kit is one spark-creatives
 * `creatives` row (`media_type='banner_kit'`) carrying the editable document (`ad_params`) and its version
 * history (`ad_versions`, newest first; a version LOCKS once generated, and editing a locked version forks a new
 * one). Wire types mirror spark-creatives `pkg/kits/types.go` (snake_case). The size catalogue and the shape plan
 * are served by the backend (`GET /creatives/banner-kits/catalog`); {@link planKitShapes} replicates the
 * backend's grouping so the cost chip matches what the run charges.
 */

/* ---------- wire types ---------- */

/** What an ad (kit or mini-game) advertises. Same categories as the video-ad subject picker. */
export type AdSubjectType = VideoSubjectType;

/** The advertised subject, snapshotted into the kit (promotion banner, game, or gamification feature). */
export interface AdSubject {
  id: string;
  name: string;
  /** The subject key art: the style and subject reference for the hero render. */
  image_url: string;
  headline?: string;
  subtitle?: string;
  cta_text?: string;
  /** The on-site landing path (promotions only), e.g. `/promotions/welcome`. */
  cta_href?: string;
}

/** The copy rendered into every size. Generation requires `confirmed` plus a headline and a CTA. */
export interface KitCopy {
  headline: string;
  subhead: string;
  cta: string;
  confirmed: boolean;
}

/** A brand colour palette (hex strings). */
export interface Palette {
  primary: string;
  accent: string;
  bg: string;
  text: string;
}

/** The brand's call-to-action button as its website draws it (from the site theme): fill and label colours (hex),
 *  an optional outline colour ('' = none), the corner radius in px (-1 when the theme names none: unknown, which the
 *  backend prompts and the runtime treat as absent, never as square) and whether it is a pill. */
export interface BrandButton {
  fill: string;
  text: string;
  border: string;
  radius_px: number;
  pill: boolean;
}

/** `BrandButton.radius_px` when the theme names no corner radius. */
export const BUTTON_RADIUS_UNKNOWN = -1;

/** The brand identity baked into the renders (the logo is reproduced exactly). The palette, button and fonts come
 *  from the brand's website theme when it has one (prefilled at create, see `brand-theme-api.ts`). */
export interface AdBrand {
  name: string;
  logo_url: string;
  /** The brand's logo variants by branding-pipeline slot (`symbol_and_name_horizontal`, `name_horizontal_black`,
   *  `symbol_and_name_vertical`...). Image Banners place the variant that fits each size best, in code (the image
   *  model never draws a logo); without it `logo_url` is the only variant. */
  logos?: Record<string, string>;
  /** The bare site host (no scheme), used in the legal line's `{domain}`. */
  domain: string;
  palette?: Palette;
  button?: BrandButton;
  font_heading?: string;
  font_body?: string;
}

/** The legal line presets (`custom` = the owner's own text). */
export type LegalPreset = 'uk_eu' | 'us' | 'br' | 'custom';

/** The code-rendered legal line drawn on every exported size. */
export interface LegalLine {
  enabled: boolean;
  preset: LegalPreset;
  /** The line's text; empty = the preset's default with `{domain}` filled in. */
  text: string;
}

/** The kits (size families) an owner can enable. Retired families, as spark-creatives reads them: a stored
 *  `affiliate` kit reads as `display` (which covers the affiliates), a stored `crm` kit is dropped (its sizes were). */
export type KitId = 'social' | 'display';

/** The kits in catalogue order (Social first: a new kit starts with it). */
export const KIT_IDS: readonly KitId[] = ['social', 'display'];

/** The default kit image model (the backend's `content-generation-kit-image-model` default). */
export const DEFAULT_KIT_MODEL = 'seedream-5-0-pro';

/** A kit's editable document (`ad_params`). */
export interface BannerKitParams {
  subject_type: AdSubjectType;
  subject: AdSubject;
  copy: KitCopy;
  brand: AdBrand;
  kits: KitId[];
  /** Sizes the owner switched off, by size id (`"970x90"`). */
  sizes_off: string[];
  art_direction: string;
  legal: LegalLine;
  model: string;
  /** The language every word of the kit is written in (a brand website language id, see ad-language.ts): its copy,
   *  CTA and legal line. '' or absent = an older kit (English-first copy, the English legal line, as before). */
  language?: AdLanguageId;
  /** The subject's copy in `language` (the website's own text, else Lumio's translation): what a Generate writes into
   *  `copy`. Absent until it has been read for this language and subject. */
  localized_copy?: LocalizedCopy;
}

/** A version's hero render (1440x1440, the style reference for every shape). `error` when it failed. */
export interface KitHero {
  url?: string;
  w?: number;
  h?: number;
  error?: string;
}

/** A normalized `[left, top, right, bottom]` box (0..1) or, for layers, pixel coordinates. */
export type KitBox = [number, number, number, number];

/** The vision QA verdict of one render. */
export type KitQAStatus = 'ok' | 'needs_review' | 'failed';

export interface KitQA {
  status: KitQAStatus;
  /** What the QA read on the render (`headline`, `cta`). */
  read?: Record<string, string> | null;
  /** Where the copy and logo sit (normalized 0..1), driving the CTA pulse of the animated export. */
  boxes?: Partial<Record<'headline' | 'subhead' | 'cta' | 'logo', KitBox>> | null;
  /** Why the render is not ok (spark-creatives `kits.QAReason*`); absent when ok, or on a render made before reasons
   *  were recorded. */
  reason?: KitQAReason;
}

/** Why a render needs a look (the first check it failed). */
export type KitQAReason =
  | 'copy'
  | 'logo_drawn'
  | 'duplicate_subject'
  | 'logo_area_copy'
  | 'logo_area_busy'
  | 'unavailable'
  | 'logo_missing';

/** One layer's edit transform (applied by the flatten). */
export interface KitLayerTransform {
  dx: number;
  dy: number;
  scale: number;
  hidden: boolean;
}

/** What a decomposed layer is. */
export type KitLayerRole = 'headline' | 'subhead' | 'cta' | 'logo' | 'subject' | 'decor';

/** A text layer's intended copy and what the QA read on it. */
export interface KitLayerText {
  intended?: string;
  read?: string;
  verified?: boolean;
}

/** One decomposed layer of a shape render (a transparent PNG positioned at `box_px` in render pixels). */
export interface KitLayer {
  id: string;
  role: KitLayerRole;
  name: string;
  url: string;
  z: number;
  box_px: KitBox;
  transform: KitLayerTransform;
  text?: KitLayerText;
}

/** A shape render decomposed into layers (kit layers; config- and flag-gated). */
export interface KitLayers {
  base_url: string;
  items: KitLayer[];
  decomposed_at: string;
}

/** One shape's render in a version. `url` is absent when it failed (`error` says why). */
export interface KitRender {
  url?: string;
  w?: number;
  h?: number;
  attempts?: number;
  qa?: KitQA;
  error?: string;
  layers?: KitLayers;
}

/** What an ad version's art was drawn with: the brand's art style and the mascot it featured, as named at draw time
 *  (the same record a website creative's version carries). Written by the run, last run wins; absent when no art
 *  style was active. `mascot_pick` is how the mascot was chosen: "" (auto), "none" or the picked mascot's id. */
export type AdVersionArtStyle = NonNullable<CreativeVersion['art_style']>;

/** One kit version (`ad_versions[]`, newest first). */
export interface BannerKitVersion {
  /** RFC 3339; the version's identity (see `versionKey`). */
  created_at: string;
  locked?: boolean;
  /** The params this version was generated from. */
  params: BannerKitParams;
  hero?: KitHero;
  /** Renders keyed by shape id (see {@link planKitShapes}). */
  shapes: Record<string, KitRender>;
  /** What the renders were drawn with (see {@link AdVersionArtStyle}). */
  art_style?: AdVersionArtStyle;
}

/** A banner kit entity as listed. */
export interface BannerKit {
  id: string;
  tenant_id: string;
  name: string;
  media_type: 'banner_kit';
  /** The poster: the newest locked version's hero. */
  image_url: string;
  /** `draft` | `ready` | `archived`. */
  status: string;
  archived: boolean;
  /** The generating job (drives the card's state and the composer's progress). */
  job_id?: string;
  created_at?: string;
  ad_params: BannerKitParams;
  ad_versions: BannerKitVersion[];
}

/** Body for creating (no `id`) or updating a kit. Blank fields are "not provided"; `ad_version` +
 *  `ad_version_at` append an unlocked version keyed by the FE-minted timestamp. */
export interface BannerKitUpsert {
  id?: string;
  name?: string;
  image_url?: string;
  status?: string;
  job_id?: string;
  ad_params?: BannerKitParams;
  ad_version?: Omit<BannerKitVersion, 'created_at' | 'locked'>;
  ad_version_at?: string;
}

/* ---------- catalogue ---------- */

/** One size of a kit. `shape` is its shape id in the full-catalogue plan (all kits on, no size off). */
export interface KitCatalogSize {
  id: string;
  w: number;
  h: number;
  /** The export weight budget for this size in this kit, in KB. */
  max_kb: number;
  shape: string;
  /** The social networks (and placements) the format fits; Social sizes only. Proper names: render raw. */
  networks?: string[];
}

export interface KitCatalogKit {
  id: KitId;
  /** English label (a data value; render it through `<Text>`). */
  label: string;
  sizes: KitCatalogSize[];
}

/** The layout family of a shape, by aspect ratio. */
export type ShapeFamily = 'strip' | 'landscape' | 'square' | 'portrait' | 'vertical' | 'skyscraper';

export interface KitCatalogShape {
  id: string;
  ratio: number;
  render_w: number;
  render_h: number;
  family: ShapeFamily;
  sizes: string[];
}

/** Backend capabilities the UI hides its buttons behind (on top of its own flags). */
export interface BannerKitFeatures {
  /** The HTML5 runtime is built (animated HTML5 export). */
  html5: boolean;
  /** Layer decomposition is enabled (kit layer editor). */
  layers: boolean;
  /** Hosted embeds are configured. */
  embed: boolean;
}

/** `GET /creatives/banner-kits/catalog`. */
export interface BannerKitCatalog {
  kits: KitCatalogKit[];
  shapes: KitCatalogShape[];
  model: string;
  price_action: string;
  /** Preset legal lines; `{domain}` is replaced with the brand domain. */
  legal_presets: Partial<Record<LegalPreset, string>>;
  features: BannerKitFeatures;
  /** The Flare image model, served only when Flare routing is enabled and priced (absent = every render on the
   *  kit's Seedream model). See {@link kitRenderModel} for which renders it draws. */
  flare_model?: string;
  /** The Flare render's price row (`kit_render:<flare_model>`). */
  flare_price_action?: string;
  /** Flare draws a render only while its aspect (w/h) is within [1/flare_max_ratio, flare_max_ratio]. */
  flare_max_ratio?: number;
}

/* ---------- export / layers ---------- */

export type BannerKitExportFormat = 'jpg' | 'png' | 'html5';

export interface BannerKitExportRequest {
  /** The version to export (its `created_at`). */
  version_at: string;
  /** Narrow to these size ids; omitted = every enabled size. */
  sizes?: string[];
  format?: BannerKitExportFormat;
  /** html5 only: the export profile id. */
  profile?: string;
  /** html5 only: the click-through written into the ad (e.g. the version's tracking link); omitted = the subject's
   *  CTA link on the brand's domain. An ad server's own clickTag wins. */
  landing_url?: string;
}

/** One exported size. `url` is absent when the size could not be exported (`error`). For `format: 'html5'`, `url` is
 *  the size's ad package (a ZIP), `max_kb` the profile's weight budget for it and `backup_url` its static backup
 *  image (the size's exported JPG). */
export interface BannerKitExportFile {
  size: string;
  url?: string;
  bytes?: number;
  format?: string;
  max_kb?: number;
  over_budget?: boolean;
  within_budget?: boolean;
  violations?: string[];
  backup_url?: string;
  error?: string;
}

export interface BannerKitExportResult {
  files: BannerKitExportFile[];
  zip_url: string;
}

/** A layer's transform as sent to the flatten. */
export type KitLayerTransformInput = KitLayerTransform & { id: string };

export interface FlattenKitLayersRequest {
  version_at: string;
  shape: string;
  transforms: KitLayerTransformInput[];
}

/** The flatten wrote the result onto a NEW locked version. */
export interface FlattenKitLayersResult {
  version_at: string;
  url: string;
}

export interface EditKitLayerRequest {
  kit_id: string;
  /** The version whose layer is edited (the FE stores the new url into a forked version itself). */
  target_version_at: string;
  shape: string;
  layer_id: string;
  /** Change a text layer to exactly this text. */
  text?: string;
  /** Or restyle the layer with this instruction. */
  instruction?: string;
}

export interface EditKitLayerResult {
  url: string;
  qa?: KitQA;
}

/** The `ad` object of the synchronous `/creatives/generate` ops (copy draft, layer edit, mini-game config fill):
 *  the job request fields plus the sync-only ones. */
export interface AdSyncRequest extends AdGenerateRequest {
  layer_id?: string;
  text?: string;
  params?: object;
}

/* ---------- shared request plumbing (also used by playable-api.ts) ---------- */

/** A refusal from a gated or charged ad endpoint. The backend answers `{ error: "<message>" }` (400/404/409; the
 *  message is owner-readable) or `{ ok: false, needed }` (402), so callers branch on `status`: 402 opens
 *  Buy-credits with `needed`, 409 is a capability that is not configured in this environment (HTML5 not built,
 *  embeds or layers not available, export media not configured), 404 an unknown entity or version. `status` 0
 *  is a network failure or an unconfigured service; `aborted` is a cancelled request. */
export interface AdApiFailure {
  ok: false;
  status: number;
  error?: string;
  needed?: number;
  aborted?: boolean;
}

/** An ad endpoint's answer: the payload, or the refusal. */
export type AdApiResult<T> = { ok: true; data: T } | AdApiFailure;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** POST/GET/DELETE an ad endpoint and fold every answer into an {@link AdApiResult} (never throws). */
export const adApiRequest = async <T>(
  method: 'get' | 'post' | 'delete',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<AdApiResult<T>> => {
  const base = creativesServiceBase();
  if (!base) return { ok: false, status: 0, error: 'creatives service not configured' };
  try {
    const config = creativesRequestConfig(signal);
    const res =
      method === 'get'
        ? await axios.get<unknown>(`${base}${path}`, config)
        : method === 'delete'
          ? await axios.delete<unknown>(`${base}${path}`, config)
          : await axios.post<unknown>(`${base}${path}`, body ?? {}, config);
    const data = res.data;
    if (res.status >= 200 && res.status < 300) {
      if (isNonJsonBody(data) || !isRecord(data)) {
        return { ok: false, status: 0, error: `${path}: the creatives service answered with a non-JSON body` };
      }
      return { ok: true, data: data as T };
    }
    const payload = isRecord(data) ? data : {};
    return {
      ok: false,
      status: res.status,
      error: typeof payload.error === 'string' ? payload.error : undefined,
      needed: typeof payload.needed === 'number' ? payload.needed : undefined,
    };
  } catch (err) {
    if (axios.isCancel(err)) return { ok: false, status: 0, aborted: true };
    return { ok: false, status: 0, error: err instanceof Error ? err.message : String(err) };
  }
};

/** The `/creatives/generate` envelope of a synchronous ad op. `prompt` is a constant: the route requires one. */
export const adSyncBody = (
  tenantId: number,
  placement: 'banner_kit' | 'playable',
  mediaType: 'banner-kit-copy' | 'kit-layer-edit' | 'playable-config',
  ad: AdSyncRequest,
): Record<string, unknown> => ({
  tenant_id: String(tenantId),
  placement,
  prompt: mediaType === 'banner-kit-copy' ? 'banner kit copy' : mediaType === 'kit-layer-edit' ? 'kit layer edit' : 'playable config',
  media_type: mediaType,
  ad,
});

/** GET a tenant list (kits / mini-games). Returns [] when the service is unconfigured; THROWS when it is
 *  unreachable or answers non-JSON (the `/__spark` dev proxy not forwarding), so a caller keeps its last list on
 *  screen instead of showing a downed backend as "none yet" (the `listVideos` lesson). */
export const listAdRows = async <R>(path: string, label: string, signal?: AbortSignal): Promise<R[]> => {
  const base = creativesServiceBase();
  if (!base) return [];
  const res = await axios.get<{ data?: R[] }>(`${base}${path}`, creativesRequestConfig(signal, (s) => s >= 200 && s < 300));
  if (isNonJsonBody(res.data)) {
    const message = `${label}: ${base} returned a non-JSON (HTML) body. The /__spark dev proxy is not forwarding or spark-creatives is unreachable.`;
    // eslint-disable-next-line no-console -- surface a masked backend/proxy outage instead of an empty list.
    console.error(`[ad-library] ${message}`);
    throw new Error(message);
  }
  return Array.isArray(res.data?.data) ? res.data.data : [];
};

/** POST a draft-first upsert (kits / mini-games). Returns the entity id ('' when unconfigured or no id came
 *  back); throws on a failed request, like `upsertVideoDraft`. */
export const upsertAdRow = async (path: string, body: object, signal?: AbortSignal): Promise<string> => {
  const base = creativesServiceBase();
  if (!base) return '';
  const res = await axios.post<{ id?: string; ok?: boolean }>(
    `${base}${path}`,
    body,
    creativesRequestConfig(signal, (s) => s >= 200 && s < 300),
  );
  return res.data?.id ?? '';
};

/* ---------- normalisation (Go nil slices arrive as null; a blank draft has `ad_params: {}`) ---------- */

const str = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
const strList = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);

const isKitId = (value: string): value is KitId => (KIT_IDS as readonly string[]).includes(value);
const isLegalPreset = (value: unknown): value is LegalPreset =>
  value === 'uk_eu' || value === 'us' || value === 'br' || value === 'custom';
const isSubjectType = (value: unknown): value is AdSubjectType =>
  value === 'promotion' || value === 'game' || value === 'gamification';

/** The subject with every field present. */
export const normaliseAdSubject = (raw: unknown): AdSubject => {
  const s = isRecord(raw) ? raw : {};
  return {
    id: str(s.id),
    name: str(s.name),
    image_url: str(s.image_url),
    headline: str(s.headline),
    subtitle: str(s.subtitle),
    cta_text: str(s.cta_text),
    cta_href: str(s.cta_href),
  };
};

/** A brand button from a wire value, or undefined when it has no fill colour (nothing to draw). A missing or negative
 *  radius is kept as unknown ({@link BUTTON_RADIUS_UNKNOWN}), not turned into a square corner. */
export const normaliseBrandButton = (raw: unknown): BrandButton | undefined => {
  if (!isRecord(raw) || typeof raw.fill !== 'string' || raw.fill === '') return undefined;
  const radius =
    typeof raw.radius_px === 'number' && Number.isFinite(raw.radius_px) && raw.radius_px >= 0
      ? raw.radius_px
      : BUTTON_RADIUS_UNKNOWN;
  return { fill: raw.fill, text: str(raw.text), border: str(raw.border), radius_px: radius, pill: raw.pill === true };
};

/** A `{slot: url}` logo set with only its non-empty string urls; undefined when none is left. Pure. */
const logoSetOf = (raw: unknown): Record<string, string> | undefined => {
  if (!isRecord(raw)) return undefined;
  const entries = Object.entries(raw).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1].trim() !== '');
  return entries.length > 0 ? Object.fromEntries(entries.map(([k, v]) => [k, v.trim()])) : undefined;
};

/** The brand with its required fields present (palette / button / fonts kept only when set). */
export const normaliseAdBrand = (raw: unknown): AdBrand => {
  const b = isRecord(raw) ? raw : {};
  const p = isRecord(b.palette) ? b.palette : undefined;
  const button = normaliseBrandButton(b.button);
  return {
    name: str(b.name),
    logo_url: str(b.logo_url),
    ...(logoSetOf(b.logos) ? { logos: logoSetOf(b.logos) } : {}),
    domain: str(b.domain),
    ...(p ? { palette: { primary: str(p.primary), accent: str(p.accent), bg: str(p.bg), text: str(p.text) } } : {}),
    ...(button ? { button } : {}),
    ...(typeof b.font_heading === 'string' && b.font_heading ? { font_heading: b.font_heading } : {}),
    ...(typeof b.font_body === 'string' && b.font_body ? { font_body: b.font_body } : {}),
  };
};

/** The legal line with a known preset (default: on, `uk_eu`). */
export const normaliseLegalLine = (raw: unknown): LegalLine => {
  const l = isRecord(raw) ? raw : {};
  return {
    enabled: typeof l.enabled === 'boolean' ? l.enabled : true,
    preset: isLegalPreset(l.preset) ? l.preset : 'uk_eu',
    text: str(l.text),
  };
};

/** Full kit params from a wire value (a blank draft's `{}` included), so every consumer sees the whole shape. */
export const normaliseBannerKitParams = (raw: unknown): BannerKitParams => {
  const p = isRecord(raw) ? raw : {};
  const c = isRecord(p.copy) ? p.copy : {};
  const kits = [...new Set(strList(p.kits).map((k) => (k === 'affiliate' ? 'display' : k)))].filter(isKitId);
  const localized = normaliseLocalizedCopy(p.localized_copy);
  return {
    subject_type: isSubjectType(p.subject_type) ? p.subject_type : 'promotion',
    subject: normaliseAdSubject(p.subject),
    copy: { headline: str(c.headline), subhead: str(c.subhead), cta: str(c.cta), confirmed: c.confirmed === true },
    brand: normaliseAdBrand(p.brand),
    kits: Array.isArray(p.kits) ? kits : ['social'],
    sizes_off: strList(p.sizes_off),
    art_direction: str(p.art_direction),
    legal: normaliseLegalLine(p.legal),
    model: str(p.model) || DEFAULT_KIT_MODEL,
    language: str(p.language),
    ...(localized ? { localized_copy: localized } : {}),
  };
};

const ART_STYLE_FIELDS = ['style_id', 'style_name', 'mascot_id', 'mascot_name', 'mascot_avatar', 'mascot_pick'] as const;

/** An ad version's `art_style` record: its string fields, or undefined when it is absent, null (the run drew with no
 *  active style) or names no style. Pure. */
export const normaliseVersionArtStyle = (raw: unknown): AdVersionArtStyle | undefined => {
  if (!isRecord(raw)) return undefined;
  const out: AdVersionArtStyle = {};
  for (const field of ART_STYLE_FIELDS) {
    const value = raw[field];
    if (typeof value === 'string') out[field] = value;
  }
  return out.style_id ? out : undefined;
};

const normaliseKitVersion = (raw: unknown): BannerKitVersion | null => {
  if (!isRecord(raw) || typeof raw.created_at !== 'string') return null;
  const artStyle = normaliseVersionArtStyle(raw.art_style);
  return {
    created_at: raw.created_at,
    locked: raw.locked === true,
    params: normaliseBannerKitParams(raw.params),
    ...(isRecord(raw.hero) ? { hero: raw.hero as KitHero } : {}),
    shapes: isRecord(raw.shapes) ? (raw.shapes as Record<string, KitRender>) : {},
    ...(artStyle ? { art_style: artStyle } : {}),
  };
};

/** A listed kit row with its params and versions normalised (versions newest first, as the backend keeps them). */
export const normaliseBannerKit = (raw: Record<string, unknown>): BannerKit => ({
  id: str(raw.id),
  tenant_id: str(raw.tenant_id, String(raw.tenant_id ?? '')),
  name: str(raw.name),
  media_type: 'banner_kit',
  image_url: str(raw.image_url),
  status: str(raw.status, 'draft'),
  archived: raw.archived === true || raw.status === 'archived',
  ...(typeof raw.job_id === 'string' && raw.job_id ? { job_id: raw.job_id } : {}),
  ...(typeof raw.created_at === 'string' ? { created_at: raw.created_at } : {}),
  ad_params: normaliseBannerKitParams(raw.ad_params),
  ad_versions: (Array.isArray(raw.ad_versions) ? raw.ad_versions : [])
    .map(normaliseKitVersion)
    .filter((v): v is BannerKitVersion => v !== null),
});

/** The catalogue from its wire value. The Flare fields are kept only together, and only when they can route a
 *  render (a model and a usable ratio bound); otherwise none is set and every render stays on Seedream. Pure. */
export const normaliseBannerKitCatalog = (raw: Record<string, unknown>): BannerKitCatalog => {
  const features = isRecord(raw.features) ? raw.features : {};
  const presets = isRecord(raw.legal_presets) ? raw.legal_presets : {};
  const legalPresets: Partial<Record<LegalPreset, string>> = {};
  for (const [key, value] of Object.entries(presets)) {
    if (isLegalPreset(key) && typeof value === 'string') legalPresets[key] = value;
  }
  const flareModel = str(raw.flare_model).trim();
  const flareMaxRatio = raw.flare_max_ratio;
  const flare =
    flareModel !== '' && typeof flareMaxRatio === 'number' && Number.isFinite(flareMaxRatio) && flareMaxRatio >= 1
      ? { flare_model: flareModel, flare_price_action: str(raw.flare_price_action), flare_max_ratio: flareMaxRatio }
      : {};
  return {
    kits: Array.isArray(raw.kits) ? (raw.kits as KitCatalogKit[]).map((k) => ({ ...k, sizes: Array.isArray(k.sizes) ? k.sizes : [] })) : [],
    shapes: Array.isArray(raw.shapes) ? (raw.shapes as KitCatalogShape[]) : [],
    model: str(raw.model) || DEFAULT_KIT_MODEL,
    price_action: str(raw.price_action),
    legal_presets: legalPresets,
    features: { html5: features.html5 === true, layers: features.layers === true, embed: features.embed === true },
    ...flare,
  };
};

/* ---------- endpoints ---------- */

const tenantPath = (tenantId: number): string => `/creatives/tenant/${encodeURIComponent(String(tenantId))}`;

/** List a brand's banner kits, newest first (archived ones flagged). See {@link listAdRows} for the error rule. */
export const listBannerKits = async (tenantId: number, signal?: AbortSignal): Promise<BannerKit[]> => {
  const rows = await listAdRows<Record<string, unknown>>(`${tenantPath(tenantId)}/banner-kits`, 'listBannerKits', signal);
  return rows.filter(isRecord).map(normaliseBannerKit);
};

/** Create (no id) or update a kit. Returns the entity id ('' when unconfigured). Throws on a failed request. */
export const upsertBannerKit = (tenantId: number, body: BannerKitUpsert, signal?: AbortSignal): Promise<string> =>
  upsertAdRow(`${tenantPath(tenantId)}/banner-kits`, body, signal);

// The catalogue is static per deploy: fetched once per page load and shared (a failure clears it for a retry).
let catalogRequest: Promise<BannerKitCatalog | null> | null = null;

/** The kit catalogue (sizes, shapes, model, legal presets, backend features). Null when the service is
 *  unconfigured or the read failed (a later call retries). */
export const fetchBannerKitCatalog = (): Promise<BannerKitCatalog | null> => {
  if (!catalogRequest) {
    catalogRequest = adApiRequest<Record<string, unknown>>('get', '/creatives/banner-kits/catalog').then((res) => {
      if (res.ok) return normaliseBannerKitCatalog(res.data);
      catalogRequest = null;
      return null;
    });
  }
  return catalogRequest;
};

/** The Lumio copy draft for a kit: headline / sub-line / CTA written from its subject (free, synchronous).
 *  `ok:false` when the model could not produce copy (the owner keeps typing their own). */
export const draftBannerKitCopy = async (
  tenantId: number,
  kitId: string,
  signal?: AbortSignal,
): Promise<{ ok: boolean; headline?: string; subhead?: string; cta?: string }> => {
  const res = await adApiRequest<{ ok?: boolean; headline?: unknown; subhead?: unknown; cta?: unknown }>(
    'post',
    '/creatives/generate',
    adSyncBody(tenantId, 'banner_kit', 'banner-kit-copy', { kit_id: kitId }),
    signal,
  );
  if (!res.ok || res.data.ok === false) return { ok: false };
  return { ok: true, headline: str(res.data.headline), subhead: str(res.data.subhead), cta: str(res.data.cta) };
};

/** Export a kit version as exact-size files plus a ZIP (free). `format: 'html5'` answers 409 until the HTML5
 *  runtime is built; 409 also when export media is not configured; 404 for an unknown version. */
export const exportBannerKit = async (
  tenantId: number,
  kitId: string,
  body: BannerKitExportRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<BannerKitExportResult>> => {
  const res = await adApiRequest<{ files?: BannerKitExportFile[] | null; zip_url?: string }>(
    'post',
    `${tenantPath(tenantId)}/banner-kits/${encodeURIComponent(kitId)}/export`,
    body,
    signal,
  );
  if (!res.ok) return res;
  return { ok: true, data: { files: Array.isArray(res.data.files) ? res.data.files : [], zip_url: str(res.data.zip_url) } };
};

/** Edit one layer of a decomposed shape: change its text or restyle it (synchronous, charged one render). 402 =
 *  not enough credits (`needed`), 409 = layer editing not available in this environment. */
export const editKitLayer = async (
  tenantId: number,
  body: EditKitLayerRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<EditKitLayerResult>> => {
  const { kit_id, target_version_at, shape, layer_id, text, instruction } = body;
  const res = await adApiRequest<{ ok?: boolean; url?: string; qa?: KitQA }>(
    'post',
    '/creatives/generate',
    adSyncBody(tenantId, 'banner_kit', 'kit-layer-edit', {
      kit_id,
      target_version_at,
      shape,
      layer_id,
      ...(text !== undefined ? { text } : {}),
      ...(instruction !== undefined ? { instruction } : {}),
    }),
    signal,
  );
  if (!res.ok) return res;
  if (res.data.ok === false || !res.data.url) return { ok: false, status: 200 };
  return { ok: true, data: { url: res.data.url, ...(res.data.qa ? { qa: res.data.qa } : {}) } };
};

/** Flatten a shape's edited layers into a new render. The backend forks `version_at` into a NEW locked version and
 *  returns its key; 409 when layers are not available. */
export const flattenKitLayers = async (
  tenantId: number,
  kitId: string,
  body: FlattenKitLayersRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<FlattenKitLayersResult>> => {
  const res = await adApiRequest<{ version_at?: string; url?: string }>(
    'post',
    `${tenantPath(tenantId)}/banner-kits/${encodeURIComponent(kitId)}/layers/flatten`,
    body,
    signal,
  );
  if (!res.ok) return res;
  return { ok: true, data: { version_at: str(res.data.version_at), url: str(res.data.url) } };
};

/* ---------- pure plan helpers ---------- */

/** One size the kit params enable, with its budget across the enabled kits and the kits it belongs to. */
export interface EnabledKitSize {
  id: string;
  w: number;
  h: number;
  /** The export weight budget: the smallest `max_kb` over the enabled kits containing the size. */
  max_kb: number;
  kits: KitId[];
}

/** The sizes the params enable: the union of the enabled kits' sizes (catalogue order, de-duplicated by id) minus
 *  `sizes_off`. */
export const enabledKitSizes = (catalog: BannerKitCatalog, params: Pick<BannerKitParams, 'kits' | 'sizes_off'>): EnabledKitSize[] => {
  const off = new Set(params.sizes_off);
  const enabled = new Set(params.kits);
  const byId = new Map<string, EnabledKitSize>();
  for (const kit of catalog.kits) {
    if (!enabled.has(kit.id)) continue;
    for (const size of kit.sizes) {
      if (off.has(size.id)) continue;
      const seen = byId.get(size.id);
      if (seen) {
        seen.max_kb = Math.min(seen.max_kb, size.max_kb);
        seen.kits.push(kit.id);
      } else {
        byId.set(size.id, { id: size.id, w: size.w, h: size.h, max_kb: size.max_kb, kits: [kit.id] });
      }
    }
  }
  return Array.from(byId.values());
};

/** One planned shape: the render one run makes for a group of near-identical aspect ratios. */
export interface PlannedKitShape {
  /** The shape id (the ratio of the group's largest member, 2 decimals): the key of `shapes` in a version. */
  id: string;
  ratio: number;
  /** The member size ids. */
  sizes: string[];
  /** The group's largest member, whose exact aspect the render keeps (the render is it scaled up; the "1" shape is
   *  the 1:1 hero). */
  w: number;
  h: number;
}

/** Sizes whose ratios are within this of a group's first ratio share one render (spark-creatives `kits.Plan`). */
const SHAPE_GROUP_TOLERANCE = 0.02;

/**
 * The shapes a run renders for these params, exactly as spark-creatives `kits.Plan` groups them: the enabled sizes
 * sorted by ratio (w/h), greedily grouped while a size's ratio is within 2 % of the group's FIRST ratio, each group
 * keyed by its largest-area member's ratio rounded to 2 decimals ("1", "1.2", "8.09"). Pass a version's own
 * `params` to map its sizes to the renders stored under `shapes`.
 */
export const planKitShapes = (catalog: BannerKitCatalog, params: Pick<BannerKitParams, 'kits' | 'sizes_off'>): PlannedKitShape[] => {
  const sizes = enabledKitSizes(catalog, params)
    .filter((s) => s.w > 0 && s.h > 0)
    .map((s) => ({ ...s, ratio: s.w / s.h }))
    .sort((a, b) => a.ratio - b.ratio);
  const groups: Array<{ first: number; members: typeof sizes }> = [];
  for (const size of sizes) {
    const current = groups[groups.length - 1];
    if (current && Math.abs(size.ratio / current.first - 1) <= SHAPE_GROUP_TOLERANCE) current.members.push(size);
    else groups.push({ first: size.ratio, members: [size] });
  }
  return groups.map(({ members }) => {
    const largest = members.reduce((a, b) => (b.w * b.h > a.w * a.h ? b : a));
    const ratio = Math.round(largest.ratio * 100) / 100;
    return { id: String(ratio), ratio, sizes: members.map((m) => m.id), w: largest.w, h: largest.h };
  });
};

/** size id -> shape id for these params (see {@link planKitShapes}). */
export const shapeIdBySize = (catalog: BannerKitCatalog, params: Pick<BannerKitParams, 'kits' | 'sizes_off'>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const shape of planKitShapes(catalog, params)) for (const size of shape.sizes) out[size] = shape.id;
  return out;
};

/** The square shape id. The hero (1440x1440) doubles as it when a run renders the hero. */
export const KIT_HERO_SHAPE_ID = '1';

/**
 * The renders one run of these shapes makes, exactly as spark-creatives renders (and charges) them: a version that
 * already has a hero renders each shape; one without renders the hero first, and the hero then doubles as the
 * square ("1") shape, so that shape is not rendered again. Empty when there is no shape (the backend refuses the
 * run, so no hero is charged either). Pure.
 */
export const kitRunRenders = <S extends { id: string }>(shapes: readonly S[], versionHasHero: boolean): Array<S | 'hero'> => {
  if (shapes.length === 0) return [];
  if (versionHasHero) return [...shapes];
  return ['hero', ...shapes.filter((s) => s.id !== KIT_HERO_SHAPE_ID)];
};

/** How many renders one run of these shape ids makes (see {@link kitRunRenders}). Pure. */
export const kitRunRenderCount = (shapeIds: readonly string[], versionHasHero: boolean): number =>
  kitRunRenders(shapeIds.map((id) => ({ id })), versionHasHero).length;

/** A render's dimensions, for its aspect (a planned shape's largest member, or a catalogue shape's render size). */
export interface KitRenderDims {
  w: number;
  h: number;
}

/**
 * The image model one kit render is drawn with, by the rule spark-creatives routes it: the catalogue's Flare model
 * when Flare is served, the brand's active art style is not mature (no active style counts as not mature) and the
 * render's aspect (w/h; the hero and the "1" shape are 1:1) is within [1/flare_max_ratio, flare_max_ratio];
 * otherwise the kit's Seedream model (`params.model`, else the catalogue's). Pure.
 */
export const kitRenderModel = (
  catalog: Pick<BannerKitCatalog, 'model' | 'flare_model' | 'flare_max_ratio'>,
  params: Pick<BannerKitParams, 'model'>,
  shape: KitRenderDims | 'hero',
  styleMature: boolean,
): string => {
  const seedream = params.model || catalog.model;
  const { flare_model: flare, flare_max_ratio: max } = catalog;
  if (!flare || max === undefined || styleMature) return seedream;
  if (shape === 'hero') return flare;
  const { w, h } = shape;
  // Compared by products, not by w/h, so a render exactly on the bound (1200x400 at 3) is never lost to rounding.
  return w > 0 && h > 0 && w <= max * h && h <= max * w ? flare : seedream;
};

/** How many renders a Generate makes (see {@link kitRunRenderCount}) for the planned shapes, narrowed to
 *  `onlyShapes` for a per-size redraw. Zero when no size is enabled (the backend refuses it). */
export const bannerKitRenderCount = (
  catalog: BannerKitCatalog,
  params: Pick<BannerKitParams, 'kits' | 'sizes_off'>,
  versionHasHero: boolean,
  onlyShapes?: readonly string[],
): number => {
  const planned = planKitShapes(catalog, params).map((s) => s.id);
  const ids = onlyShapes ? planned.filter((id) => onlyShapes.includes(id)) : planned;
  return kitRunRenderCount(ids, versionHasHero);
};

/** The credit cost of a Generate, or of a redraw of `onlyShapes`: each render the run makes ({@link kitRunRenders})
 *  at its own model's `kit_render` price ({@link kitRenderModel}, so a Flare render and a Seedream one price
 *  differently). Undefined until the catalogue and every needed price are known, so the cost chip stays hidden
 *  rather than showing 0 or a partial sum. */
export const bannerKitCostFor = (
  catalog: BannerKitCatalog | null | undefined,
  params: BannerKitParams,
  versionHasHero: boolean,
  pricing: CreditPricing | null | undefined,
  styleMature: boolean,
  onlyShapes?: readonly string[],
): number | undefined => {
  if (!catalog) return undefined;
  const planned = planKitShapes(catalog, params);
  const renders = kitRunRenders(onlyShapes ? planned.filter((s) => onlyShapes.includes(s.id)) : planned, versionHasHero);
  // Nothing to render (no size enabled): 0, but only once the kit's price has loaded, as before.
  if (renders.length === 0) return kitRenderCostFor(pricing, params.model || catalog.model) === undefined ? undefined : 0;
  let total = 0;
  for (const render of renders) {
    const price = kitRenderCostFor(pricing, kitRenderModel(catalog, params, render, styleMature));
    if (price === undefined) return undefined;
    total += price;
  }
  return total;
};

/** Whether a version has a usable hero (a run into it reuses the hero instead of rendering a new one). */
export const versionHasUsableHero = (version: Pick<BannerKitVersion, 'hero'> | undefined): boolean =>
  Boolean(version?.hero?.url && !version.hero.error);

/** Whether a draft (unlocked) version's hero was drawn with another character than `pick` ('' is Auto) or another
 *  art style than `styleId` ('' is none): a Generate does not reuse it, or the picked character / the active style
 *  would never be drawn into the kit. A generated version is never reused anyway. Pure. */
export const heroDrawnWithOther = (
  version: Pick<BannerKitVersion, 'hero' | 'locked' | 'art_style'> | undefined,
  pick: string,
  styleId: string,
): boolean =>
  Boolean(
    version &&
      version.locked !== true &&
      versionHasUsableHero(version) &&
      ((version.art_style?.mascot_pick ?? '') !== pick || (version.art_style?.style_id ?? '') !== styleId),
  );

const sameText = (a: string | undefined, b: string | undefined): boolean => (a ?? '').trim() === (b ?? '').trim();

/**
 * Whether going from params `a` to `b` changes what the hero is painted from: the copy, the art direction, the
 * subject, the brand (logo, name, palette, CTA button style), the model or the language (every word is painted in
 * it). Every shape is an edit of the hero, so renders made from a hero with the OLD values are stale and a new hero
 * must be rendered. The kits, the sizes and the legal line do not reach the art (the legal line is drawn at export),
 * and neither does confirming the copy. Pure.
 */
export const heroAffectingChanged = (a: BannerKitParams, b: BannerKitParams): boolean =>
  !sameText(a.copy.headline, b.copy.headline) ||
  !sameText(a.copy.subhead, b.copy.subhead) ||
  !sameText(a.copy.cta, b.copy.cta) ||
  !sameText(a.art_direction, b.art_direction) ||
  a.subject.id !== b.subject.id ||
  a.subject.image_url !== b.subject.image_url ||
  a.brand.logo_url !== b.brand.logo_url ||
  a.brand.name !== b.brand.name ||
  JSON.stringify(a.brand.palette ?? null) !== JSON.stringify(b.brand.palette ?? null) ||
  // The CTA button style is part of the hero prompt's theme sentence.
  JSON.stringify(a.brand.button ?? null) !== JSON.stringify(b.brand.button ?? null) ||
  (a.model || DEFAULT_KIT_MODEL) !== (b.model || DEFAULT_KIT_MODEL) ||
  (a.language ?? '').trim() !== (b.language ?? '').trim();

/** A version entry ready for an upsert's `ad_version` (no identity, no lock). */
export type BannerKitVersionEntry = Omit<BannerKitVersion, 'created_at' | 'locked'>;

/**
 * The entry a kit version forks into when its document becomes `next`: the source's renders (hero and shapes, deep
 * copied) are kept while they still match `next`, so the grid keeps showing them and the next run reuses the hero;
 * a change that reaches the hero (see {@link heroAffectingChanged}) starts from no renders, so the next run paints
 * a new hero instead of editing the stale one. Pure.
 */
export const kitForkEntry = (source: BannerKitVersion, next: BannerKitParams): BannerKitVersionEntry => {
  const params: BannerKitParams = JSON.parse(JSON.stringify(next)) as BannerKitParams;
  if (heroAffectingChanged(source.params, next)) return { params, shapes: {} };
  const copy = JSON.parse(
    JSON.stringify({ hero: source.hero, shapes: source.shapes, art_style: source.art_style }),
  ) as Pick<BannerKitVersion, 'hero' | 'shapes' | 'art_style'>;
  // The kept renders were drawn with the source's art style, so the fork still says so.
  return {
    params,
    ...(copy.hero ? { hero: copy.hero } : {}),
    shapes: copy.shapes ?? {},
    ...(copy.art_style ? { art_style: copy.art_style } : {}),
  };
};

/** A legal preset's line with `{domain}` filled in; '' for `custom` or an unknown preset. */
export const legalPresetText = (catalog: BannerKitCatalog | null | undefined, preset: LegalPreset, domain: string): string =>
  preset === 'custom' ? '' : (catalog?.legal_presets[preset] ?? '').split('{domain}').join(domain);
